import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { CheckCircle, Clock } from 'lucide-react';
import { toast } from "react-toastify";
import { saveTeacherReviewedSubjectiveQuizResults } from "ServerAPI/serverAPI";

const SubjectiveQuizResultViewer = ({
    quizData,
    courseId,
    userId,
    contentId
}) => {
    const [editedScores, setEditedScores] = useState({});
    const [savedScores, setSavedScores] = useState({});  // Stores scores after API save
    const [isSaving, setIsSaving] = useState(false);
    const [teacherReviewedState, setTeacherReviewedState] = useState(null);
    const [expandedQuestions, setExpandedQuestions] = useState({});

    // === Extract sectionId from quizData ===
    const sectionId =
        quizData?.quiz?.sectionId ||
        quizData?.localContent?.sectionId ||
        quizData?.sectionId ||
        null;

    // === Extract teacherReviewed from quiz level (controls editing) ===
    // Check multiple possible locations for teacherReviewed flag
    const teacherReviewedFromData = 
        quizData?.quiz?.teacherReviewed === true ||
        quizData?.localContent?.teacherReviewed === true ||
        quizData?.localContent?.quizResults?.teacherReviewed === true ||
        quizData?.quizResults?.teacherReviewed === true ||
        quizData?.teacherReviewed === true;
    
    const quizTeacherReviewed = teacherReviewedState !== null
        ? teacherReviewedState
        : teacherReviewedFromData;

    // === Extract Questions & Results Safely ===
    const questions =
        quizData?.quiz?.questions ||
        quizData?.questions ||
        quizData?.data?.questions ||
        quizData?.localContent?.questions ||
        [];

    const userResults =
        quizData?.localContent?.quizResults?.results ||
        quizData?.quizResults?.results ||
        quizData?.results ||
        quizData?.data?.results ||
        [];

    // === Combine Data ===
    const combinedData = userResults.map((result, index) => {
        const question = questions.find(q =>
            [q._id, q.id, q.questionId].includes(result.questionId) ||
            q.id?.toString() === result.questionId
        ) || questions[index] || {};

        const questionId = result.questionId;

        return {
            ...result,
            questionId,
            questionText: question?.question || question?.text || `Question ${index + 1}`,
            modelAnswer: question?.modelAnswer || question?.correctAnswer || "Model answer not available",
            maxMarks: question?.totalMarks || question?.marks || 1,
            currentScore: editedScores[questionId] !== undefined
                ? editedScores[questionId]
                : (savedScores[questionId] !== undefined
                    ? savedScores[questionId]
                    : (result.final_score || result.overall_score || 0))
        };
    });

    const handleScoreChange = (questionId, newScore, maxMarks) => {
        // ONLY block editing if teacherReviewed is TRUE
        if (quizTeacherReviewed === true) return;

        const score = Math.max(0, Math.min(maxMarks, parseFloat(newScore) || 0));
        setEditedScores(prev => ({ ...prev, [questionId]: score }));
    };

    const handleSaveScores = async () => {
        if (Object.keys(editedScores).length === 0) return;

        setIsSaving(true);

        const updatedScores = Object.entries(editedScores).map(([questionId, score]) => ({
            questionId: String(questionId),
            overall_score: Number(score)
        }));

        const payload = {
            userId,
            courseId,
            sectionId,
            contentId,
            updatedScores,
            teacherReviewed: true
        };

        console.log("=== Saving Teacher Reviewed Scores ===");
        console.log("Payload:", JSON.stringify(payload, null, 2));

        try {
            const response = await saveTeacherReviewedSubjectiveQuizResults(payload);
            console.log("API Response:", response);

            toast.success("Scores saved & finalized successfully!");

            // Store the saved scores from API response
            const responseData = response?.data || response;
            const resultsFromResponse = responseData?.quizResults?.results || [];

            const newSavedScores = {};
            resultsFromResponse.forEach(r => {
                newSavedScores[r.questionId] = r.final_score || r.overall_score || 0;
            });
            setSavedScores(prev => ({ ...prev, ...newSavedScores }));

            setEditedScores({});

            // After successful save, teacherReviewed becomes true - disable further edits
            setTeacherReviewedState(true);

        } catch (err) {
            console.error("Save error:", err);
            console.error("Error response:", err.response?.data);
            toast.error(err.response?.data?.message || "Failed to save scores.");
        } finally {
            setIsSaving(false);
        }
    };

    if (combinedData.length === 0) {
        return <div className="text-center py-5 text-muted">No subjective quiz data found.</div>;
    }

    return (
        <div className="subjective-quiz-review container-fluid p-0">

            {/* Save Button - Only show if teacherReviewed is FALSE */}
            {!quizTeacherReviewed && Object.keys(editedScores).length > 0 && (
                <div className="mb-3 text-end">
                    <button
                        className="btn btn-primary px-4"
                        onClick={handleSaveScores}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : `Save & Finalize (${Object.keys(editedScores).length} changed)`}
                    </button>
                </div>
            )}

            {combinedData.map((item, index) => {
                return (
                    <Card key={item.questionId || index} className="mb-4 shadow-sm" style={{ border: '1px solid #e5e7eb' }}>
                        <Card.Body className="p-4">

                            {/* Header */}
                            <div className="d-flex justify-content-between align-items-start mb-3">
                                <h5 className="fw-normal text-dark mb-0" style={{ lineHeight: '1.6', flex: 1 }}>
                                    <span className="fw-bold me-2">Q{index + 1}:</span>
                                    {item.questionText}
                                </h5>

                                <div className="d-flex align-items-center ms-3" style={{ minWidth: '160px' }}>
                                    <div className="border rounded px-2 py-1 d-flex align-items-center bg-white"
                                        style={{ borderColor: '#d1d5db' }}>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm text-center fw-bold fs-5"
                                            style={{ width: '60px', border: 'none', padding: '0' }}
                                            value={item.currentScore}
                                            onChange={(e) => handleScoreChange(item.questionId, e.target.value, item.maxMarks)}
                                            min="0"
                                            max={item.maxMarks}
                                            step="0.1"
                                            disabled={quizTeacherReviewed === true}
                                        />
                                        <span className="text-muted ms-1 small">/ {item.maxMarks} M</span>
                                    </div>

                                    {quizTeacherReviewed ? (
                                        <div className="ms-2 rounded-circle p-1" style={{ backgroundColor: '#d1fae5' }}>
                                            <CheckCircle size={20} className="text-success" />
                                        </div>
                                    ) : (
                                        <div className="ms-2 rounded-circle p-1" style={{ backgroundColor: '#fef3c7' }}>
                                            <Clock size={20} className="text-warning" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* User Answer */}
                            <div className="mb-3">
                                <p className="mb-1 text-primary fw-bold small">User Answer:</p>
                                <p className="text-dark mb-0" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                    {item.userAnswer || "No answer provided."}
                                </p>
                            </div>

                            {/* Model Answer */}
                            <div className="mb-3">
                                <p className="mb-1 text-success fw-bold small">Model Answer:</p>
                                <p className="text-muted mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                    {item.modelAnswer}
                                </p>
                            </div>

                            {/* Detailed Feedback */}
                            {item.evaluation?.criteria_explanations && (
                                <div className="mt-3 pt-3 border-top">
                                    <small
                                        className="text-primary fw-bold"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setExpandedQuestions(prev => ({ ...prev, [index]: !prev[index] }))}
                                    >
                                        {expandedQuestions[index] ? "▼ Hide Detailed Feedback" : "▶ Show Detailed Feedback"}
                                    </small>

                                    {expandedQuestions[index] && (
                                        <div className="mt-2">
                                            {item.evaluation.criteria_explanations.map((crit, i) => (
                                                <div key={i} className="mb-2 p-2 bg-light rounded">
                                                    <div className="d-flex justify-content-between">
                                                        <span className="text-dark small text-capitalize fw-bold">
                                                            {crit.criterion_name?.replace(/_/g, ' ')}
                                                        </span>
                                                        <span className="text-primary small fw-bold">
                                                            {crit.marks_obtained || 0} marks
                                                        </span>
                                                    </div>
                                                    {crit.marks_given_reason && (
                                                        <div className="text-success small mt-1">
                                                            ✓ {crit.marks_given_reason}
                                                        </div>
                                                    )}
                                                    {crit.marks_deducted_reason && crit.marks_deducted_reason !== "None" && crit.marks_deducted_reason !== "None." && (
                                                        <div className="text-danger small mt-1">
                                                            ✗ {crit.marks_deducted_reason}
                                                        </div>
                                                    )}
                                                    {crit.improvements_needed && crit.improvements_needed.length > 0 && (
                                                        <div className="text-warning small mt-1">
                                                            💡 Improvements: {crit.improvements_needed.join(", ")}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                );
            })}
        </div>
    );
};

export default SubjectiveQuizResultViewer;